/**
 * FileWidget component.
 * @module components/manage/Widgets/FileWidget
 *
added aria- attributes
 */

import React from 'react';
import loadable from '@loadable/component';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { readAsDataURL } from 'promise-file-reader';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';
import { Dimmer, Image, Message } from 'semantic-ui-react';
import Button from 'volto-form-block/components/Widget/Button';

import formBlockMessages from '../../../../messages/formBlock';

import deleteSVG from '@plone/volto/icons/delete.svg';

const imageMimetypes = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/jpg',
  'image/gif',
  'image/svg+xml',
];

// Customização: restringe extensões permitidas para upload.
const ALLOWED_EXTENSIONS = [
  'gif',
  'jpeg',
  'jpg',
  'png',
  'webp',
  'doc',
  'xls',
  'csv',
  'ppt',
  'docx',
  'xlsx',
  'pptx',
  'odt',
  'ods',
  'odp',
  'otp',
  'pdf',
  'txt',
  'zip',
];

// Customização: limita o tamanho máximo de upload para 5MB.
const MAX_FILE_SIZE = 5 * 1024 * 1024;
// Fim da customização

const Dropzone = loadable(() => import('react-dropzone'));

const messages = defineMessages({
  releaseDrag: {
    id: 'Drop files here ...',
    defaultMessage: 'Drop files here ...',
  },
  editFile: {
    id: 'Drop file here to replace the existing file',
    defaultMessage: 'Drop file here to replace the existing file',
  },
  fileDrag: {
    id: 'Drop file here to upload a new file',
    defaultMessage: 'Drop file here to upload a new file',
  },
  replaceFile: {
    id: 'Replace existing file',
    defaultMessage: 'Replace existing file',
  },
  addNewFile: {
    id: 'Choose a file',
    defaultMessage: 'Choose a file',
  },
});

// Customização: helper para validar extensão.
const getFileExtension = (filename) => {
  if (!filename) return '';
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};
// Fim da customização

/**
 * FileWidget component class.
 * @function FileWidget
 * @returns {string} Markup of the component.
 *
 * To use it, in schema properties, declare a field like:
 *
 * ```jsx
 * {
 *  title: "File",
 *  widget: 'file',
 * }
 * ```
 * or:
 *
 * ```jsx
 * {
 *  title: "File",
 *  type: 'object',
 * }
 * ```
 */
const FileWidget = (props) => {
  const { id, value, onChange, required, invalid } = props;
  const [fileType, setFileType] = React.useState(false);
  // Customização: estado para exibir erro de validação do arquivo.
  const [fileError, setFileError] = React.useState(null);
  // Fim da customização
  const intl = useIntl();

  React.useEffect(() => {
    if (value && imageMimetypes.includes(value['content-type'])) {
      setFileType(true);
    }
  }, [value]);

  const imgsrc = value?.download
    ? `${flattenToAppURL(value?.download)}?id=${Date.now()}`
    : null || value?.data
      ? `data:${value['content-type']};${value.encoding},${value.data}`
      : null;

  // Customização: valida tamanho e extensão antes de processar o upload.
  const validateFile = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      return intl.formatMessage(formBlockMessages.fileTooLarge, {
        size: 5,
      });
    }

    const extension = getFileExtension(file.name);
    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      return intl.formatMessage(formBlockMessages.invalidExtension, {
        extensions: ALLOWED_EXTENSIONS.join(', '),
      });
    }

    return null;
  };
  // Fim da customização

  /**
   * Drop handler
   * @method onDrop
   * @param {array} files File objects
   * @returns {undefined}
   */
  const onDrop = (files) => {
    const file = files[0];

    // Customização: interrompe upload ao falhar na validação.
    const validationError = validateFile(file);
    if (validationError) {
      setFileError(validationError);
      onChange(id, null);
      setFileType(false);
      return;
    }

    setFileError(null);
    // Fim da customização

    readAsDataURL(file).then((data) => {
      const fields = data.match(/^data:(.*);(.*),(.*)$/);
      onChange(id, {
        data: fields[3],
        encoding: fields[2],
        'content-type': fields[1],
        filename: file.name,
      });
    });

    let reader = new FileReader();
    reader.onload = function () {
      const fields = reader.result.match(/^data:(.*);(.*),(.*)$/);
      if (imageMimetypes.includes(fields[1])) {
        setFileType(true);
        let imagePreview = document.getElementById(`field-${id}-image`);
        imagePreview.src = reader.result;
      } else {
        setFileType(false);
      }
    };
    reader.readAsDataURL(files[0]);

    /* add dummy file to input element,
       to make sure the browsers form validation
       recognizes this field as filled
    */
    let inputEl = document.getElementById(`field-${id}`);
    const myFile = new File([], file.name, {
      type: file.type,
      lastModified: new Date(),
    });
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(myFile);
    inputEl.files = dataTransfer.files;
    // help Safari out by setting the data-file attribute
    if (inputEl.webkitEntries.length) {
      inputEl.dataset.file = `${dataTransfer.files[0].name}`;
    }
  };

  let attributes = {};
  if (required) {
    attributes.required = true;
    attributes['aria-required'] = true;
  }

  // Customização: erro de validação local também marca o campo como inválido.
  const isInvalid = invalid === true || invalid === 'true' || !!fileError;
  // Fim da customização
  if (isInvalid) {
    attributes['aria-invalid'] = true;
  }

  return (
    <FormFieldWrapper {...props}>
      <Dropzone onDrop={onDrop}>
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div className="file-widget-dropzone" {...getRootProps()}>
            {isDragActive && <Dimmer active></Dimmer>}
            {fileType ? (
              <Image
                className="image-preview"
                id={`field-${id}-image`}
                size="small"
                src={imgsrc}
              />
            ) : (
              <div className="dropzone-placeholder">
                {isDragActive ? (
                  <p className="dropzone-text">
                    {intl.formatMessage(messages.releaseDrag)}
                  </p>
                ) : value ? (
                  <p className="dropzone-text">
                    {intl.formatMessage(messages.editFile)}
                  </p>
                ) : (
                  <p className="dropzone-text">
                    {intl.formatMessage(messages.fileDrag)}
                  </p>
                )}
              </div>
            )}

            <label className="label-file-widget-input">
              {value
                ? intl.formatMessage(messages.replaceFile)
                : intl.formatMessage(messages.addNewFile)}
            </label>
            <input
              {...getInputProps({
                type: 'file',
                style: { display: 'none' },
              })}
              id={`field-${id}`}
              name={id}
              type="file"
              // Customização: restringe seletor de arquivos no input nativo.
              accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
              // Fim da customização
              {...attributes}
            />
          </div>
        )}
      </Dropzone>

      {/* Customização: exibe orientação de upload para o usuário final. */}
      <p className="text-base mt-1" aria-live="polite">
        {intl.formatMessage(formBlockMessages.allowedExtensions, {
          extensions: ALLOWED_EXTENSIONS.join(', '),
        })}{' '}
        {/* {intl.formatMessage(formBlockMessages.maxFileSize, { size: 30 })} */}
      </p>
      {/* Fim da customização */}

      {/* Customização: exibe erro de validação de arquivo. */}
      {fileError && (
        <Message negative size="tiny" role="alert" aria-live="assertive">
          <p>{fileError}</p>
        </Message>
      )}
      {/* Fim da customização */}

      <div className="field-file-name">
        {value && value.filename}
        {value && (
          <Button
            icon
            basic
            className="delete-button"
            aria-label="delete file"
            onClick={() => {
              onChange(id, null);
              setFileType(false);
              // Customização: limpa o erro ao remover o arquivo.
              setFileError(null);
              // Fim da customização
            }}
          >
            <Icon name={deleteSVG} size="20px" />
          </Button>
        )}
      </div>
    </FormFieldWrapper>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
FileWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.shape({
    '@type': PropTypes.string,
    title: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  wrapped: PropTypes.bool,
};

/**
 * Default properties.
 * @property {Object} defaultProps Default properties.
 * @static
 */
FileWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
};

export default injectIntl(FileWidget);
